import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { useSnackbar } from "notistack";
import React, { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  setClientID,
  setProjectID,
} from "../../../../../Redux/Actions/actions";
import "../../../../SnackbarAlerts/styles/render-snackbar.css";
import useCurrentProject from "../../../DashboardComponents/Form/FormQueries/useCurrentProject";
import { DashboardContext } from "../../../DashboardContext/DashboardContextProvider";
import useSrf from "../../../QueryHooks/srf/useSrf";
import useUpdateSrf from "../../../QueryHooks/srf/useUpdateSrf";
import useInvalidInputs from "../../../QueryHooks/validate/useInvalidInputs";
import PageValues from "./PageValues";
import PdfContainer from "./PdfContainer";
import "./styles/validate-request.css";

// Main Validate Request Component
const ValidateRequest = () => {
  useEffect(() => setHeading("Validate Service Request Form"), []);
  const [pageNumber, setPageNumber] = useState(1);
  const [numPages, setNumPages] = useState(1);
  const [invalidInputs, setInvalidInputs] = useState([]);
  const [hoveredChipName, setHoveredChipName] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [clearInvalid, setClearInvalid] = useState(false);

  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  // Dashboard Context
  const { setHeading, navigate, clientId, projectId, isAdmin, queryClient } =
    useContext(DashboardContext);

  // Form Data Query
  const { data: formData, isFetched: formFetched } = useCurrentProject(
    clientId,
    projectId,
  );

  // Srf Query
  const {
    data: srf,
    isFetched: srfFetched,
    isFetching: srfFetching,
  } = useSrf(clientId, projectId);

  // Update Srf Mutation
  const updateSrf = useUpdateSrf(clientId, projectId);

  // Invalid Inputs Query
  const { data: invalidData, isFetched: invalidFetched } = useInvalidInputs(
    clientId,
    projectId,
  );

  const adminReview = useMutation({
    mutationFn: async (status) => {
      try {
        console.log("ADMIN REVIEW STATUS: ", status);
        const res = await axios.patch(
          `/api/admin-review/${clientId}/${projectId}/`,
          { requestStatus: status },
          { withCredentials: true },
        );

        return res.data;
      } catch (err) {
        console.log("ADMIN REVIEW ERR: ", err);
      }
    },
    onError: (err) => {
      console.log("ADMIN REVIEW ERR: ", err);
      enqueueSnackbar("Error updating status", {
        variant: "error",
      });
    },
    onSuccess: (data) => {
      enqueueSnackbar("Status updated successfully", {
        variant: "success",
      });
      setConfirmOpen(false);
      dispatch(setClientID(null));
      dispatch(setProjectID(null));
      queryClient.invalidateQueries({ queryKey: ["adminRows"] });
      navigate(`/dashboard/admin`, { replace: true });
    },
  });

  // Function to determine the completion status of the questionnaire
  const getProjectStatus = async (e) => {
    e.preventDefault();
    let invalidInputsLength = invalidInputs.length;
    if (invalidInputsLength > 0) {
      // Mutate Request Status to "RV" if inputs are invalid
      try {
        await adminReview.mutateAsync("RV");
      } catch (error) {
        enqueueSnackbar("Error updating request status to 'Revisions Needed'", {
          variant: "error",
        });
      }
    } else {
      try {
        // Mutate Request Status to "C" if all inputs are valid
        await adminReview.mutateAsync("C");
      } catch (error) {
        enqueueSnackbar("Error updating request status to 'Complete'", {
          variant: "error",
        });
      }
    }
  };

  return formFetched && formData ? (
    <>
      <Modal
        id="submit-srf-modal"
        classes={{
          root: "submit-srf-modal",
          paperClasses: "submit-srf-paper",
        }}
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        aria-labelledby="submit-srf-check-modal"
        aria-describedby="submit-srf-check-modal"
      >
        <div className="submit-srf-modal-content">
          <header id="check-submit-header">
            <h1 id="submit-check-header">Validate SRF</h1>
            <hr id="check-submit-hr" />
          </header>
          <div id="check-submit-body">
            <p id="check-submit-message">
              {invalidInputs.length === 0
                ? "Are you sure you want to submit? This will set the status of the request to 'Complete', and the client will be able to download/view the srf."
                : "Are you sure you want to submit? This will set the status of the request to 'Revise', and the client will be able to edit the form."}
            </p>
            <div id="check-submit-buttons">
              <Button
                id="cancel-submit-srf-btn"
                variant="contained"
                color="primary"
                onClick={() => setConfirmOpen(false)}
              >
                Cancel
              </Button>
              <Button
                id="validate-submit-srf-btn"
                variant="contained"
                color="primary"
                onClick={getProjectStatus}
                disabled={adminReview.isPending}
              >
                Submit
              </Button>
            </div>
          </div>
        </div>
      </Modal>
      <div id="main-validation-container">
        <PageValues
          clientId={clientId}
          projectId={projectId}
          pageNumber={pageNumber}
          setPageNumber={setPageNumber}
          numPages={numPages}
          formData={formData}
          setHoveredChipName={setHoveredChipName}
          updateSrf={updateSrf}
          enqueueSnackbar={enqueueSnackbar}
          invalidData={invalidData}
          invalidFetched={invalidFetched}
          setConfirmOpen={setConfirmOpen}
          invalidInputs={invalidInputs}
          setInvalidInputs={setInvalidInputs}
          clearInvalid={clearInvalid}
          setClearInvalid={setClearInvalid}
          srfFetching={srfFetching}
        />
        <PdfContainer
          isAdmin={isAdmin}
          pageNumber={pageNumber}
          setPageNumber={setPageNumber}
          numPages={numPages}
          setNumPages={setNumPages}
          hoveredChipName={hoveredChipName}
          updateSrf={updateSrf}
          invalidData={invalidData}
          invalidFetched={invalidFetched}
          srf={srf}
          srfFetched={srfFetched}
        />
      </div>
    </>
  ) : (
    <p>Loading...</p>
  );
};

export default ValidateRequest;
